import { TFunction } from 'i18next';
import { compact, flatten } from 'lodash';

import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { GovernmentIdSide } from 'lib/interfaces/verificationConfig';

import { StepGovernmentIdConfig } from './interfaces/StepsConfig';

/**
 * Certain countries have special names for their ID types, so the translation
 * may be localized per-country. This handles that safely, by falling back to generic names.
 */
export function tForGovernmentId(
  t: TFunction,
  rootKeys: string[],
  countryCode?: CountryCode,
  governmentIdClass?: GovernmentIdClass | null,
  governmentIdSide?: GovernmentIdSide,
) {
  const countryCodeTKey = countryCode?.toLowerCase();
  const governmentIdClassTKey = governmentIdClass;
  const governmentIdSideTKey = ((governmentIdSide) => {
    switch (governmentIdSide) {
      case GovernmentIdSide.Back:
        return 'back';

      case GovernmentIdSide.BarcodePdf417:
        return 'barcode';

      case GovernmentIdSide.Front:
        return 'front';

      case GovernmentIdSide.FrontOrBack:
        return 'front-or-back';

      case GovernmentIdSide.PassportSignature:
        return 'passport-signature';
    }
  })(governmentIdSide);

  const tKeys = flatten(
    rootKeys.map((key) => {
      // Translation keys are from most specific to least specific.
      return compact([
        // countryCode.governmentIdClass.governmentIdSide
        countryCodeTKey && governmentIdClassTKey && governmentIdSideTKey
          ? `${key}.${countryCodeTKey}.${governmentIdClassTKey}.${governmentIdSideTKey}`
          : null,
        // countryCode.governmentIdClass.default
        countryCodeTKey && governmentIdClassTKey
          ? `${key}.${countryCodeTKey}.${governmentIdClassTKey}.default`
          : null,
        // countryCode.unknown.governmentIdSide
        countryCodeTKey && governmentIdSideTKey
          ? `${key}.${countryCodeTKey}.unknown.${governmentIdSideTKey}`
          : null,
        // countryCode.unknown.default
        countryCodeTKey ? `${key}.${countryCodeTKey}.unknown.default` : null,
        // countryCode.governmentIdClass
        countryCodeTKey && governmentIdClassTKey
          ? `${key}.${countryCodeTKey}.${governmentIdClassTKey}`
          : null,
        // countryCode.unknown
        countryCodeTKey ? `${key}.${countryCodeTKey}.unknown` : null,
        // governmentIdClass.governmentIdSide
        governmentIdClassTKey && governmentIdSideTKey
          ? `${key}.${governmentIdClassTKey}.${governmentIdSideTKey}`
          : null,
        // governmentIdClass.default
        governmentIdClassTKey ? `${key}.${governmentIdClassTKey}.default` : null,
        // unknown.governmentIdSide
        governmentIdSideTKey ? `${key}.unknown.${governmentIdSideTKey}` : null,
        // unknown.default
        `${key}.unknown.default`,
        // governmentIdClass
        governmentIdClassTKey ? `${key}.${governmentIdClassTKey}` : null,
        // unknown
        `${key}.unknown`,
      ]);
    }),
  );

  // eslint-disable-next-line @persona-id/t-string-only
  return t(tKeys);
}

export function getCaptureFrameLabel(
  localizations: StepGovernmentIdConfig['localizations']['capture-page' | 'check-page'],
  governmentIdClass?: GovernmentIdClass,
  governmentIdSide?: GovernmentIdSide,
) {
  if (
    (governmentIdClass === 'pp' || governmentIdClass === 'ipp') &&
    governmentIdSide === GovernmentIdSide.Front
  ) {
    return localizations['passport-portrait-capture-frame-label'];
  } else if (
    (governmentIdClass === 'pp' || governmentIdClass === 'ipp') &&
    governmentIdSide === GovernmentIdSide.PassportSignature
  ) {
    return localizations['passport-signature-capture-frame-label'];
  } else if (governmentIdSide === GovernmentIdSide.BarcodePdf417) {
    return localizations['barcode-capture-frame-label'];
  } else if (governmentIdSide === GovernmentIdSide.Back) {
    return localizations['id-back-capture-frame-label'];
  } else {
    return localizations['id-front-capture-frame-label'];
  }
}
